import { useState, useEffect, useCallback } from "react";
import { Edit, Save, Smartphone, Bell, Clock, Play, Send, AlertCircle, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { api } from "./adminApi";

export default function SmsTemplatesTab() {
  const [templates, setTemplates] = useState([]);
  const [editTemplate, setEditTemplate] = useState(null);
  const [content, setContent] = useState("");

  // J-1 scheduler state
  const [j1Status, setJ1Status] = useState(null);
  const [j1Time, setJ1Time] = useState("17:00");
  const [j1Enabled, setJ1Enabled] = useState(true);
  const [j1Running, setJ1Running] = useState(false);
  const [j1LastResult, setJ1LastResult] = useState(null);
  const [savingJ1, setSavingJ1] = useState(false);

  // SMS test panel state
  const [smsStatus, setSmsStatus] = useState(null);
  const [testPhone, setTestPhone] = useState("");
  const [testing, setTesting] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      const [t, jr, ss] = await Promise.all([
        api.get("/sms-templates"),
        api.get("/admin/j1-reminder/status"),
        api.get("/admin/sms/status"),
      ]);
      setTemplates(t.data);
      setJ1Status(jr.data);
      setJ1Time(jr.data?.time || "17:00");
      setJ1Enabled(!!jr.data?.enabled);
      setSmsStatus(ss.data);
    } catch { toast.error("Impossible de charger les templates SMS"); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const openEdit = (t) => { setEditTemplate(t); setContent(t.content || ""); };
  const insertPlaceholder = (ph) => setContent((prev) => prev + ph);

  const handleSave = async () => {
    if (!content.trim()) { toast.error("Le contenu ne peut pas être vide"); return; }
    try {
      await api.put(`/sms-templates/${editTemplate.template_key}`, { content });
      toast.success("Template SMS sauvegardé");
      setEditTemplate(null);
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  const saveJ1Time = async () => {
    if (!/^\d{2}:\d{2}$/.test(j1Time)) { toast.error("Format invalide. Utilise HH:MM"); return; }
    setSavingJ1(true);
    try {
      await api.patch("/admin/settings", { key: "J1_SMS_REMINDER_TIME", value: j1Time });
      toast.success("Heure du scheduler mise à jour");
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
    finally { setSavingJ1(false); }
  };

  const toggleJ1Enabled = async () => {
    const next = !j1Enabled;
    setJ1Enabled(next);
    setSavingJ1(true);
    try {
      await api.patch("/admin/settings", { key: "J1_SMS_REMINDER_ENABLED", value: next ? "true" : "false" });
      toast.success(next ? "Scheduler activé" : "Scheduler désactivé");
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
    finally { setSavingJ1(false); }
  };

  const runJ1Now = async (dryRun) => {
    setJ1Running(true);
    setJ1LastResult(null);
    try {
      const r = await api.post("/admin/j1-reminder/run", { dry_run: !!dryRun });
      setJ1LastResult(r.data);
      const d = r.data;
      if (dryRun) toast.message(`Simulation : ${d.offers_found} offre(s), ${d.details?.length || 0} SMS prévus`);
      else toast.success(`Scan terminé : ${d.sms_sent} SMS envoyés (${d.sms_skipped_already_sent} déjà envoyés, ${d.sms_skipped_no_consent} sans consentement, ${d.sms_skipped_no_phone} sans téléphone)`);
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur lors du scan"); }
    finally { setJ1Running(false); }
  };

  const handleTestSms = async () => {
    if (!testPhone.trim()) { toast.error("Numéro requis"); return; }
    setTesting(true);
    try {
      const r = await api.post("/admin/settings/test-sms", { phone: testPhone.trim() });
      const d = r.data;
      if (d.status === "sent") toast.success(`SMS envoyé ✅ (${d.credits_used || 1} crédits)`);
      else if (d.status === "simulated") toast.message(`Simulé (clé Brevo non configurée)`);
      else toast.error(`Erreur : ${d.error || d.message}`);
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
    finally { setTesting(false); }
  };

  const charCount = content.length;
  const segments = charCount <= 160 ? 1 : Math.ceil((charCount - 160) / 153) + 1;

  return (
    <div data-testid="sms-templates-tab" className="space-y-6">
      {/* Brevo status banner */}
      {smsStatus && (
        <div className={`p-3 border ${smsStatus.configured ? "bg-green-50 border-green-200" : "bg-amber-50 border-amber-200"}`}>
          <div className="flex items-center gap-2">
            {smsStatus.configured ? <CheckCircle2 className="w-4 h-4 text-green-600" /> : <AlertCircle className="w-4 h-4 text-amber-600" />}
            <p className={`text-sm font-semibold ${smsStatus.configured ? "text-green-800" : "text-amber-800"}`}>
              Brevo SMS : {smsStatus.configured ? `Configuré ✅ (Sender: ${smsStatus.sender_id})` : "Non configuré — les SMS seront simulés"}
            </p>
          </div>
        </div>
      )}

      {/* J-1 Scheduler */}
      <div className="bg-white border border-gray-200 p-5" data-testid="j1-reminder-section">
        <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
          <h3 className="text-base font-bold text-[#3F3F3F] flex items-center gap-2">
            <Bell className="w-4 h-4 text-[#662483]" />
            Rappel SMS J-1 automatique
          </h3>
          {j1Status?.scheduler?.running && (
            <span className="text-[11px] px-2 py-0.5 bg-green-100 text-green-700 border border-green-200">Scheduler actif</span>
          )}
        </div>
        <p className="text-xs text-gray-500 mb-4">
          Tous les jours à l'heure définie, le système envoie un SMS de rappel (template <em>« Rappel J-1 »</em> ci-dessous) aux candidats affectés à une mission qui démarre le lendemain.
        </p>
        <div className="grid sm:grid-cols-2 gap-4 mb-4">
          <div>
            <Label className="text-sm font-semibold text-[#3F3F3F] flex items-center gap-1.5">
              <Clock className="w-3.5 h-3.5" /> Heure d'envoi (Europe/Paris)
            </Label>
            <div className="flex gap-2 mt-2">
              <Input type="time" value={j1Time} onChange={(e) => setJ1Time(e.target.value)} className="rounded-none flex-1 max-w-[140px]" data-testid="j1-time-input" />
              <Button onClick={saveJ1Time} disabled={savingJ1} className="bg-gradient-to-r from-[#662483] to-[#0069B4] text-white rounded-none text-xs h-9" data-testid="j1-save-time-btn">
                <Save className="w-3.5 h-3.5 mr-1" /> Enregistrer
              </Button>
            </div>
            {j1Status?.scheduler?.jobs?.[0]?.next_run_time && (
              <p className="text-[11px] text-gray-400 mt-2">
                Prochain run : {new Date(j1Status.scheduler.jobs[0].next_run_time).toLocaleString("fr-FR")}
              </p>
            )}
          </div>
          <div>
            <Label className="text-sm font-semibold text-[#3F3F3F]">Activation</Label>
            <p className="text-[11px] text-gray-500 mt-0.5 mb-2">Mettre en pause / réactiver le scheduler</p>
            <Button onClick={toggleJ1Enabled} variant="outline" disabled={savingJ1} className={`rounded-none text-xs h-9 w-fit ${j1Enabled ? "border-green-500 text-green-700" : "border-gray-300 text-gray-500"}`} data-testid="j1-toggle-btn">
              {j1Enabled ? "✓ Activé (cliquer pour désactiver)" : "○ Désactivé (cliquer pour activer)"}
            </Button>
          </div>
        </div>
        <div className="border-t border-gray-100 pt-4 flex flex-wrap gap-2">
          <Button onClick={() => runJ1Now(true)} disabled={j1Running} variant="outline" className="rounded-none text-xs h-9" data-testid="j1-dry-run-btn">
            {j1Running ? "Scan…" : "Simuler maintenant (dry-run)"}
          </Button>
          <Button onClick={() => runJ1Now(false)} disabled={j1Running} className="bg-gradient-to-r from-[#662483] to-[#0069B4] text-white rounded-none text-xs h-9" data-testid="j1-run-now-btn">
            <Play className="w-3.5 h-3.5 mr-1" /> {j1Running ? "Envoi…" : "Lancer le scan maintenant"}
          </Button>
        </div>
        {j1LastResult && (
          <div className="mt-4 bg-gray-50 border border-gray-200 p-3 text-xs" data-testid="j1-last-result">
            <p className="font-semibold text-[#3F3F3F] mb-1">
              Dernier scan {j1LastResult.dry_run ? "(simulation)" : ""} — date cible : {j1LastResult.target_date}
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 text-center mt-2">
              <div className="bg-white border p-2"><div className="text-xl font-bold text-[#662483]">{j1LastResult.offers_found}</div><div className="text-[10px] text-gray-500">Offres trouvées</div></div>
              <div className="bg-white border p-2"><div className="text-xl font-bold text-green-600">{j1LastResult.sms_sent}</div><div className="text-[10px] text-gray-500">SMS envoyés</div></div>
              <div className="bg-white border p-2"><div className="text-xl font-bold text-amber-600">{j1LastResult.sms_skipped_already_sent}</div><div className="text-[10px] text-gray-500">Déjà envoyés</div></div>
              <div className="bg-white border p-2"><div className="text-xl font-bold text-gray-500">{j1LastResult.sms_skipped_no_consent + j1LastResult.sms_skipped_no_phone}</div><div className="text-[10px] text-gray-500">Ignorés</div></div>
              <div className="bg-white border p-2"><div className="text-xl font-bold text-red-600">{j1LastResult.sms_errors}</div><div className="text-[10px] text-gray-500">Erreurs</div></div>
            </div>
          </div>
        )}
      </div>

      {/* SMS Templates list */}
      <div>
        <h3 className="text-base font-bold text-[#3F3F3F] mb-2 flex items-center gap-2">
          <Smartphone className="w-4 h-4 text-[#662483]" /> Modèles de SMS
        </h3>
        <p className="text-sm text-gray-500 mb-4">
          Personnalisez les SMS envoyés automatiquement. Utilisez les <strong>placeholders</strong> entre accolades pour insérer dynamiquement le lieu, la date, etc.
        </p>
        <div className="space-y-3">
          {templates.map((t) => (
            <div key={t.template_key} className="bg-white border border-gray-200 p-4" data-testid={`sms-template-${t.template_key}`}>
              <div className="flex items-start justify-between gap-3 mb-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <Smartphone className="w-4 h-4 text-[#662483]" />
                    <h4 className="text-sm font-bold text-[#3F3F3F]">{t.label || t.template_key}</h4>
                  </div>
                  <p className="text-[11px] text-gray-500 mt-1">{t.description}</p>
                </div>
                <button onClick={() => openEdit(t)} className="p-2 hover:bg-gray-100 text-[#0069B4] shrink-0" data-testid={`edit-sms-template-${t.template_key}`}>
                  <Edit className="w-4 h-4" />
                </button>
              </div>
              <div className="bg-[#FAFAFA] border border-gray-100 p-3 text-xs text-gray-700 font-mono whitespace-pre-wrap">
                {t.content}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* SMS Test panel */}
      <div className="bg-white border border-gray-200 p-5">
        <h3 className="text-base font-bold text-[#3F3F3F] mb-1 flex items-center gap-2">
          <Send className="w-4 h-4 text-[#0069B4]" /> Test SMS Brevo
        </h3>
        <p className="text-xs text-gray-500 mb-3">Envoyer un SMS de test pour valider la configuration. Coûte des crédits Brevo (si configurée).</p>
        <div className="flex gap-2 flex-wrap">
          <Input value={testPhone} onChange={(e) => setTestPhone(e.target.value)} placeholder="0612345678 ou +33612345678" className="rounded-none flex-1 min-w-[200px]" data-testid="test-sms-phone" />
          <Button onClick={handleTestSms} disabled={testing} className="bg-gradient-to-r from-[#662483] to-[#0069B4] text-white rounded-none" data-testid="test-sms-send-btn">
            <Send className="w-4 h-4 mr-2" /> {testing ? "Envoi…" : "Envoyer un SMS test"}
          </Button>
        </div>
      </div>

      {/* Edit dialog */}
      <Dialog open={!!editTemplate} onOpenChange={() => setEditTemplate(null)}>
        <DialogContent className="sm:max-w-xl rounded-none max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-[#3F3F3F]">Modifier : {editTemplate?.label || editTemplate?.template_key}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-sm font-semibold mb-1 block">Contenu du SMS</Label>
              <Textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                className="rounded-none min-h-[140px] font-mono text-sm"
                placeholder="Texte du SMS avec placeholders {title}, {location}, {mission_start}..."
                data-testid="sms-template-content"
              />
              <div className="flex items-center justify-between mt-1">
                <p className="text-[11px] text-gray-500">
                  {charCount} caractère{charCount > 1 ? "s" : ""} — {segments} segment{segments > 1 ? "s" : ""}
                </p>
                {charCount > 160 && (
                  <p className="text-[11px] text-amber-600">⚠️ Plus de 160 car. = plusieurs SMS facturés</p>
                )}
              </div>
            </div>
            <div>
              <Label className="text-xs text-gray-600 mb-2 block">Placeholders disponibles (cliquez pour insérer)</Label>
              <div className="flex flex-wrap gap-1">
                {(editTemplate?.placeholders || []).map((ph) => (
                  <button key={ph} type="button" onClick={() => insertPlaceholder(ph)} className="px-2 py-1 text-[11px] font-mono bg-[#F0E9F3] text-[#662483] border border-[#662483]/20 hover:bg-[#662483] hover:text-white transition-colors" data-testid={`insert-placeholder-${ph}`}>
                    {ph}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex gap-3 pt-2">
              <Button onClick={handleSave} className="bg-gradient-to-r from-[#662483] to-[#0069B4] text-white rounded-none font-semibold" data-testid="save-sms-template-btn">
                <Save className="w-4 h-4 mr-2" /> Sauvegarder
              </Button>
              <Button variant="outline" onClick={() => setEditTemplate(null)} className="rounded-none">Annuler</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
